import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@/store/store";
import type Widget from "@/model/widget";
import { setWidgets } from "@/store/widget-list-slice";
import { QueryKeys } from "@/constants";
import widgetList from "@/services/home/widget";
import useUserInfo from "@/hooks/use-user-info";

export function useWidgetBootstrap() {
  const dispatch = useDispatch();
  const userInfo = useUserInfo();

  const activePlanCode = useSelector(
    (state: RootState) =>
      state.planCoverageInfo?.activeCoverage?.claimCoverageCode ?? null,
  );

  const widgetsQuery = useQuery({
    queryKey: [QueryKeys.WIDGETS, userInfo.username],
    queryFn: () => widgetList(userInfo),
    enabled: Boolean(userInfo?.username) && Boolean(userInfo?.accessToken),
    staleTime: 30 * 60 * 1000,
  });

  useEffect(() => {
    if (!widgetsQuery.isSuccess || !widgetsQuery.data) return;
    dispatch(setWidgets({ byPlan: widgetsQuery.data }));
  }, [widgetsQuery.isSuccess]);

  return {
    isLoading: widgetsQuery.isLoading || !activePlanCode,
    isError: widgetsQuery.isError,
    isReady: widgetsQuery.isSuccess && !!activePlanCode,
  };
}

export function useWidgets() {
  const { isLoading, isError, isReady } = useWidgetBootstrap();

  const activePlanCode = useSelector(
    (state: RootState) =>
      state.planCoverageInfo?.activeCoverage?.claimCoverageCode ?? null,
  );
  const byPlan = useSelector(
    (state: RootState) => state.widgetList?.byPlan ?? {},
  );

  const data = useMemo((): Widget[] => {
    if (!activePlanCode || !byPlan[activePlanCode]) return [];
    return byPlan[activePlanCode];
  }, [byPlan, activePlanCode]);

  return { isLoading, isError, isSuccess: isReady, data };
}

export default useWidgets;
